/**
 * A league run by the OCDL Discord bot: its draft boards (one per pool), rosters and budgets, schedule and trades. It's
 * read from the bot's database, so it follows the draft on Discord, refreshing every few seconds while it's drafting.
 */
import type { PokemonData } from '@ogreclan/pokemon-core';
import { useEffect, useState } from 'preact/hooks';
import { api, signInUrl, type BotLeague, type BotPool } from '../api';
import { DivisionBanner, divisionOf } from '../divisions';
import { useMe } from '../me';
import { loadPokemon, Sprite } from '../pokemon';
import { Link, navigate } from '../router';
import { ActionButton, Countdown, ErrorBox, Loading, toast } from '../ui';
import { BotMatches } from './discord-matches';
import { BotStandings } from './discord-standings';
import { BotTrades } from './discord-trades';
import { BotEditPick, BotPickPanel, BotQueuePanel, BotTierList, usePrices } from './discord-picks';


export const BOT_STATUS: Record<string, [string, string]> = {
	setup: ['Setting up', 'warn'], active: ['Drafting', 'good'], complete: ['Drafted', 'accent'], archived: ['Finished', ''],
	cancelled: ['Cancelled', 'bad'],
};
export const BOT_MODES: Record<string, string> = {
	draft_only: 'Draft only', draft_schedule: 'Draft and schedule', full_season: 'Full season',
};

/** A Discord league's address: its Showdown room's (/league/tealdivision) when it has one, or its number's */
export function botLeagueAddress(league: { id: number, room: string | null }) {
	return league.room ? `/league/${league.room}` : `/discord/${league.id}`;
}

const TABS = [
	['board', 'Board'], ['tiers', 'Tier list'], ['rosters', 'Rosters'], ['matches', 'Matches'], ['standings', 'Standings'], ['trades', 'Trades'],
] as const;
type TabId = typeof TABS[number][0];

function duration(seconds: number) {
	if (seconds % 3600 === 0) return `${seconds / 3600} hour${seconds === 3600 ? '' : 's'}`;
	if (seconds % 60 === 0) return `${seconds / 60} minutes`;
	return `${seconds} seconds`;
}

/** A Pokémon as the bot spells it ("Mega Eternal-Floette", "Meowstic-M"), with its sprite when Showdown's data knows the name */
export function BotMon({ data, name }: { data: PokemonData | null, name: string }) {
	const species = data?.getSpecies(name);
	return <span class="mon">
		{species && <Sprite data={data} species={species} />}
		<span class="name">{name}</span>
	</span>;
}

function Board({ pool, data, rounds, onChoose }: {
	pool: BotPool, data: PokemonData | null, rounds: number, onChoose?: (playerId: number, round: number) => void,
}) {
	const players = [...pool.players].sort((a, b) => a.seat - b.seat);
	if (!players.length) return <p class="empty">No players yet.</p>;
	const shown = Math.max(rounds, pool.status === 'active' ? pool.round : 0, ...pool.picks.map(p => p.round));
	return <div class="table-wrap"><table class="board">
		<thead><tr><th>Round</th>{players.map(p => <th key={p.id}>{p.id === pool.you ? <strong>{p.name}</strong> : p.name}
			{p.status === 'withdrawn' && <span class="muted small"> (withdrew)</span>}</th>)}</tr></thead>
		<tbody>{Array.from({ length: shown }, (_, i) => i + 1).map(round => <tr key={round}>
			<td class="muted">{round}</td>
			{players.map(player => {
				const pick = pool.picks.find(p => p.playerId === player.id && p.round === round);
				const skipped = pool.skips.some(s => s.playerId === player.id && s.round === round);
				const next = pool.status === 'active' && round === pool.round && pool.onTheClock === player.id;
				return <td key={player.id} class={`${!pick && skipped ? 'skipped' : ''} ${next ? 'next' : ''} ${onChoose ? 'choosable' : ''}`}
					onClick={onChoose && (() => onChoose(player.id, round))} title={onChoose ? `Edit ${player.name}'s round ${round} pick` : undefined}>
					{pick ? <>
						<BotMon data={data} name={pick.pokemon} />
						{pick.teraCaptain && <span class="star" title="Tera Captain"> ★</span>}
						<span class="muted small"> {pick.cost}{pick.makeup ? ' · late' : ''}{pick.auto ? ' · queued' : ''}</span>
					</> : next ? <span class="muted">On the clock</span> : skipped ? 'Skipped' : ''}
				</td>;
			})}
		</tr>)}</tbody>
	</table></div>;
}

function Rosters({ league, pool, data }: { league: BotLeague, pool: BotPool, data: PokemonData | null }) {
	return <div class="grid">
		{[...pool.players].sort((a, b) => a.seat - b.seat).map(player => <div key={player.id} class="card"
			style={player.id === pool.you ? 'outline:2px solid var(--accent)' : ''}>
			<div class="row" style="justify-content:space-between">
				<h3 style="margin:0">{player.name}</h3>
				<span class="muted small">Seat {player.seat}</span>
			</div>
			<p class="muted small" style="margin:2px 0 0">
				{player.budgetRemaining}/{league.budget} points left
				{league.teraBudget > 0 && <> · {player.teraBudgetRemaining}/{league.teraBudget} Tera</>}
				{' · '}{player.roster.length} Pokémon
				{player.owedRounds.length > 0 && <> · <span class="badge warn">owed round {player.owedRounds.join(', ')}</span></>}
				{player.status === 'withdrawn' && <> · <span class="badge">withdrew</span></>}
			</p>
			<ul class="roster">
				{player.roster.map(mon => <li key={mon.pokemon}>
					<BotMon data={data} name={mon.pokemon} />
					{mon.teraCaptain && <span class="star" title="Tera Captain">★</span>}
					{mon.source === 'trade' && <span class="badge">traded</span>}
					<span class="pts">{mon.cost} pts</span>
				</li>)}
				{!player.roster.length && <li class="empty">No Pokémon yet.</li>}
			</ul>
		</div>)}
	</div>;
}

/** Staff: start a pool's draft now, or at a time (the bot starts it by itself then) */
function StartDraft({ league, pool, reload }: { league: BotLeague, pool: BotPool, reload: () => void }) {
	const [at, setAt] = useState('');
	const start = async (when: number | null) => {
		try {
			await api.post(`/api/bot-leagues/${league.id}/pools/${encodeURIComponent(pool.label)}/start`, { at: when });
			toast(when ? `Pool ${pool.label} starts ${new Date(when * 1000).toLocaleString()}.` : `Pool ${pool.label}'s draft has started.`, 'good');
			reload();
		} catch (error) {
			toast((error as Error).message, 'bad');
		}
	};
	return <div class="card">
		<h3 style="margin-top:0">Staff: start pool {pool.label}'s draft</h3>
		<div class="row">
			<ActionButton class="primary" confirm={`Start pool ${pool.label}'s draft now?`} action={() => start(null)}>Start now</ActionButton>
			<span class="small muted">or at</span>
			<input type="datetime-local" value={at} onInput={e => setAt((e.target as HTMLInputElement).value)} />
			<ActionButton disabled={!at} action={() => start(Math.floor(new Date(at).getTime() / 1000))}>Schedule</ActionButton>
		</div>
		<p class="small muted">Players can queue picks before it starts; a queued first pick is made the moment it does.</p>
	</div>;
}

export function DiscordLeaguePage({ id, tab }: { id: string, tab?: string }) {
	const [league, setLeague] = useState<BotLeague | null>(null);
	const [error, setError] = useState<unknown>(null);
	const [poolLabel, setPoolLabel] = useState('');
	const view: TabId = TABS.find(([key]) => key === tab)?.[0] ?? 'board';
	const [chosen, setChosen] = useState<{ playerId: number, round: number } | null>(null);
	const [data, setData] = useState<PokemonData | null>(null);
	const [reloads, setReloads] = useState(0);
	const me = useMe();
	useEffect(() => {
		void loadPokemon('champions').then(setData);
	}, []);

	useEffect(() => {
		let timer: ReturnType<typeof setTimeout>;
		let stopped = false;
		const load = async () => {
			clearTimeout(timer);
			try {
				const loaded = await api.get<BotLeague>(`/api/bot-leagues/${encodeURIComponent(id)}`);
				if (stopped) return;
				setLeague(loaded);
				setError(null);
				// quickly while a draft is on, slowly otherwise
				timer = setTimeout(load, loaded.pools.some(p => p.status === 'active') ? 5000 : 60000);
			} catch (e) {
				if (!stopped) setError(e);
			}
		};
		void load();
		return () => {
			stopped = true;
			clearTimeout(timer);
		};
	}, [id, reloads]);
	// a league with a Showdown room lives at that room's address
	useEffect(() => {
		if (league?.room && location.pathname.startsWith('/discord/')) {
			navigate(`${botLeagueAddress(league)}${view === 'board' ? '' : `/${view}`}${location.search}`, true);
		}
	}, [league?.room]);
	// now, and again once the server has copied the bot's database (every couple of seconds)
	const reload = () => {
		setReloads(n => n + 1);
		setTimeout(() => setReloads(n => n + 1), 2500);
	};
	const shownPool = league && (league.pools.find(p => p.label === poolLabel) || league.pools.find(p => p.you !== null) || league.pools[0]);
	const isStaff = !!(me?.user?.isAdmin || me?.user?.isModerator || league?.youHost);
	const prices = usePrices(league, shownPool?.you != null || isStaff ? shownPool : null);

	if (error) return <><ErrorBox error={error} /><p><Link href="/league">All leagues</Link></p></>;
	if (!league) return <Loading />;
	const pool = shownPool;
	const everyone = league.pools.flatMap(p => p.players);
	const nameOf = (playerId: number | null) => everyone.find(p => p.id === playerId)?.name || 'Someone';
	const [statusText, statusClass] = BOT_STATUS[league.status] || [league.status, ''];
	const onTheClock = pool?.players.find(p => p.id === pool.onTheClock);
	const you = pool?.players.find(p => p.id === pool.you);
	const drafting = !!pool && pool.status === 'active' && !!you && you.status !== 'withdrawn';
	const canPick = league.website && drafting && you!.roster.length < league.maxPicks &&
		(pool.onTheClock === you!.id || you!.owedRounds.length > 0);
	const canQueue = league.website && !!pool && ['setup', 'active'].includes(pool.status) && !!you && you.status !== 'withdrawn' &&
		you.roster.length < league.maxPicks;

	const division = divisionOf(league);

	return <>
		<div class="league-head">
			<div>
				<p class="small" style="margin:0"><Link href="/league">Leagues</Link> / Discord</p>
				{division ? <DivisionBanner division={division}><h1>{league.name}</h1></DivisionBanner> : <h1>{league.name}</h1>}
				{me?.user && <p class="small" style="margin:0 0 4px"><Link href={`/chat?page=${botLeagueAddress(league)}`}>Ask OgreClan AI about this league</Link></p>}
				<p class="muted small" style="margin:0">
					<span class={`badge ${statusClass}`}>{statusText}</span>{' '}
					{league.private && <><span class="badge" title="Only its players, signed in with Discord, can see it">Private</span>{' '}</>}
					{league.siteOnly && <><span class="badge accent" title="Made on the site, with the bot's rules and no Discord">Site league</span>{' '}</>}
					{BOT_MODES[league.mode] || league.mode} · {league.budget} points{league.teraBudget > 0 && `, ${league.teraBudget} Tera`} ·{' '}
					{league.minPicks === league.maxPicks ? league.maxPicks : `${league.minPicks}–${league.maxPicks}`} Pokémon each ·{' '}
					{duration(league.timer)} a pick
					{league.currentWeek > 0 && <> · <strong>week {league.currentWeek}</strong></>}
					{league.docUrl && <> · <a href={league.docUrl} target="_blank" rel="noreferrer">Spreadsheet</a></>}
				</p>
			</div>
		</div>
		{league.pools.length > 1 && <nav class="tabs">
			{league.pools.map(p => <a key={p.label} href="#" class={p === pool ? 'active' : ''} onClick={event => {
				event.preventDefault();
				setPoolLabel(p.label);
			}}>Pool {p.label}{p.you !== null && ' (yours)'}</a>)}
		</nav>}
		{pool && pool.freeTradesUntil && pool.freeTradesUntil * 1000 > Date.now() && <p class="notice good">
			<strong>Free trade window</strong>{league.pools.length > 1 && ` (pool ${pool.label})`}: for{' '}
			<Countdown until={pool.freeTradesUntil * 1000} /> more, trades and pickups are unlimited
			{league.maxTrades > 0 && ` and don't count toward the limit of ${league.maxTrades} Pokémon picked up`}. Propose them with /trade on Discord.
		</p>}
		{pool && <>
			{pool.status === 'active' && onTheClock && <div class={`clock ${onTheClock.id === pool.you ? 'yours' : ''}`}>
				<span class="big">{onTheClock.id === pool.you ? `You're on the clock` : `${onTheClock.name} is on the clock`}</span>
				<span>Round {pool.round}</span>
				{pool.turnDeadline && <span>Time left: <strong><Countdown until={pool.turnDeadline * 1000} /></strong></span>}
				<span class="muted small">{onTheClock.id === pool.you ? 'Pick below or on Discord' : 'Picks are made here or on Discord'}</span>
			</div>}
			{pool.status === 'setup' && pool.scheduledStart && <p class="notice">The draft starts {new Date(pool.scheduledStart * 1000).toLocaleString()}.</p>}
			{!league.website && <p class="notice">This league doesn't use the site for picks or matches: they're made on Discord.</p>}
			{isStaff && pool.status === 'setup' && <StartDraft league={league} pool={pool} reload={reload} />}
			{['setup', 'active'].includes(pool.status) && me && !me.user && <p class="notice">
				Playing in this draft? <a href={signInUrl()}>Sign in with Discord</a> to make your picks and queue them here.</p>}
			{canPick && <BotPickPanel league={league} pool={pool} me={you!} data={data} prices={prices} reload={reload} />}
			{canQueue && <BotQueuePanel league={league} pool={pool} me={you!} data={data} prices={prices} reload={reload} />}
			{isStaff && pool.status === 'active' && onTheClock && onTheClock.id !== pool.you &&
				<BotPickPanel league={league} pool={pool} me={onTheClock} data={data} prices={prices} reload={reload} staff />}
		</>}
		<nav class="tabs">
			{TABS.map(([key, label]) => <Link key={key} href={`${botLeagueAddress(league)}${key === 'board' ? '' : `/${key}`}`}
				class={view === key ? 'active' : ''}>{label}</Link>)}
		</nav>
		{pool && view === 'board' && <div class="card">
			<h2 style="margin-top:0">Board{league.pools.length > 1 && `: pool ${pool.label}`}</h2>
			<Board pool={pool} data={data} rounds={league.maxPicks} onChoose={isStaff ? (playerId, round) => setChosen({ playerId, round }) : undefined} />
		</div>}
		{pool && view === 'board' && isStaff && pool.status !== 'setup' &&
			<BotEditPick league={league} pool={pool} prices={prices} chosen={chosen} reload={reload} />}
		{pool && view === 'tiers' && <BotTierList league={league} pool={pool} data={data} />}
		{pool && view === 'rosters' && <Rosters league={league} pool={pool} data={data} />}
		{view === 'matches' && <BotMatches league={league} reloadLeague={reload} />}
		{view === 'standings' && <BotStandings league={league} data={data} />}
		{view === 'trades' && <BotTrades league={league} staff={isStaff} prices={prices} reload={reload} />}
	</>;
}
